import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { SITE, NAV_LINKS } from "@/data/site";
import { BurlyLogo } from "@/components/ui-burly/BurlyLogo";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 bg-[#FFFFFF] transition-shadow ${
        scrolled ? "border-b border-[#E2E3EE]" : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
        <div className="flex h-20 items-center justify-between gap-8">
          <Link
            to="/"
            data-testid="brand-logo-link"
            className="flex items-center gap-3 group"
          >
            <BurlyLogo size="h-10 w-[120px]" surface="light" testId="navbar-burly-logo" />
            <span className="sr-only">{SITE.brand}</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.slice(1).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.18em] transition-colors ${
                    isActive ? "text-[#1A1B2E]" : "text-[#5B5E78] hover:text-[#1A1B2E]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center">
            <Link
              to="/contact/"
              data-testid="nav-cta-contact"
              className="inline-flex items-center px-5 py-2.5 bg-[#1A1B2E] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#282760] transition-colors"
            >
              Request a quote
            </Link>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden p-2 -mr-2 text-[#1A1B2E]"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-[#E2E3EE] bg-[#FFFFFF]"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `text-base uppercase tracking-[0.18em] ${
                    isActive ? "text-[#1A1B2E]" : "text-[#5B5E78]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact/"
              data-testid="mobile-nav-cta"
              className="mt-2 inline-flex items-center justify-center px-5 py-3 bg-[#1A1B2E] text-white text-xs uppercase tracking-[0.2em]"
            >
              Request a quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
